import { useState } from "react";
import { Users, ChevronDown, ChevronUp } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const PAGE_LABELS = {
  "/dashboard": "Dashboard",
  "/leads": "Leads",
  "/kanban": "Pipeline",
  "/proposals": "Proposals",
  "/quotes": "Quotes",
  "/invoices": "Invoices",
  "/projects": "Projects",
  "/clients": "Clients",
  "/calendar": "Calendar",
  "/crew": "Crew",
  "/settings": "Settings",
  "/marketing": "Marketing",
  "/reporting": "Reporting",
  "/catalog": "Catalog",
  "/contracts": "Contracts",
  "/mission-control": "Mission Control",
};

function getPageLabel(path) {
  if (!path) return "";
  for (const [prefix, label] of Object.entries(PAGE_LABELS)) {
    if (path.startsWith(prefix)) return label;
  }
  return path.split("/")[1] || "";
}

/**
 * PresenceIndicator — Shows online team members in the sidebar.
 * Compact: just avatars + count. Expandable to show names and pages.
 */
export function PresenceIndicator({ onlineUsers = [], currentUserId }) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);

  // Filter out current user
  const others = onlineUsers.filter(u => u.user_id !== currentUserId);
  if (others.length === 0) return null;

  return (
    <div className="mx-3 mb-2" data-testid="presence-indicator">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--app-bg-secondary)] transition-colors"
      >
        <div className="flex -space-x-1.5">
          {others.slice(0, 4).map((u, i) => (
            <div key={u.user_id} className="relative">
              {u.avatar ? (
                <img src={u.avatar} alt="" className="w-5 h-5 rounded-full border border-[var(--app-bg)] object-cover" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-blue-500/20 border border-[var(--app-bg)] flex items-center justify-center">
                  <span className="text-[8px] font-bold text-blue-400">{(u.name || u.email)?.[0]?.toUpperCase() || "?"}</span>
                </div>
              )}
              <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-500 border border-[var(--app-bg)]" />
            </div>
          ))}
        </div>
        <span className="text-[10px] text-[var(--app-text-muted)] flex-1 text-left">
          {others.length} {t("online")}
        </span>
        {expanded ? <ChevronUp className="w-3 h-3 text-[var(--app-text-muted)]" /> : <ChevronDown className="w-3 h-3 text-[var(--app-text-muted)]" />}
      </button>

      {expanded && (
        <div className="mt-1 space-y-0.5 animate-in fade-in slide-in-from-top-1 duration-200">
          {others.map(u => (
            <div key={u.user_id} className="flex items-center gap-2 px-2 py-1 rounded-md">
              {u.avatar ? (
                <img src={u.avatar} alt="" className="w-4 h-4 rounded-full object-cover" />
              ) : (
                <div className="w-4 h-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <span className="text-[7px] font-bold text-blue-400">{(u.name || u.email)?.[0]?.toUpperCase()}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-[var(--app-text)] truncate">{u.name || u.email}</p>
              </div>
              <span className="text-[9px] text-[var(--app-text-muted)] truncate max-w-[60px]">{getPageLabel(u.page)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
